<template>
    <a :class="{
            [$style.wrapper]: true, 
            [$style.halfWidthCard]: isJobDescOpen,
            [$style.viewingCard]: isCurrentlyOpenJob && isJobDescOpen,
        }" @click="emitOpenJob(job)">
        <div :class="$style.innerWrapper">
            <div v-if="tagState" :class="$style.mobileCtaRow">
                <Tag
                    v-if="tagState"
                    :color="tagState.color"
                    :class="$style.tag"
                    :text="tagState.text"
                />
                <div :class="[$style.jobPostedDateLabel, $style.formattedDateMobileOnly]">
                    <span>{{ newDate }}</span>
                </div>
            </div>
            <div style="display:flex;">
                <div :class="$style.cardContentWrapper">
                    <div :class="$style.logoWrapper">
                        <img
                            v-if="showLogo"
                            :class="$style.companyLogo"
                            v-bind:src="job.orgLogo"
                            alt="company logo"
                            @error="showLogo = false"
                        />
                        <div v-else :class="$style.companyLogo" />
                    </div>
                    <div :class="$style.cardTop">
                        <div style="display:flex;">
                            <h3 :class="[$style.jobTitle, $style.truncateWithEllipsis]">
                                {{ job.title }}
                            </h3>
                            <div
                                v-if="!tagState"
                                :class="[$style.jobPostedDateLabel, $style.formattedDateMobileOnly]"
                            >
                                <span>{{ newDate }}</span>
                            </div>
                        </div>
                        <p :class="[$style.truncateWithEllipsis, $style.orgName]">
                            {{ job.orgName }}
                        </p>
                        <div :class="$style.detailsContainer">
                            <div :class="$style.jobDetail" v-if="hasJobLocation">
                                <span>
                                    <FontAwesomeIcon :icon="faMapMarkerAlt" />
                                </span>
                                <div>
                                    <div :class="$style.location">
                                        <span
                                            v-if="job.remotePreferences"
                                            :class="$style.remotePreferences"
                                        >
                                            {{ job.remotePreferences }}&nbsp;·&nbsp;
                                        </span>
                                        <span
                                            :class="[
                                                $style.truncateWithEllipsis,
                                                $style.jobLocation,
                                            ]"
                                        >
                                            {{ jobLocationDescription }}
                                        </span>
                                    </div>
                                    <div :class="$style.locationDetails">
                                        {{ jobLocationDetails }}
                                    </div>
                                </div>
                            </div>
                            <p
                                :class="{
                                    [$style.jobDetail]: true,
                                    [$style.salaryOnlyDetails]: !hasSalary && hasSalaryDetails,
                                }"
                                v-if="salary"
                            >
                                <span>
                                    <FontAwesomeIcon :icon="faMoneyBill" />
                                </span>
                                {{ salary }}
                            </p>
                        </div>
                    </div>
                </div>
                <div :class="[$style.ctaRow, $style.formattedDateDesktopOnly]">
                    <div :class="$style.jobPostedDateLabel">
                        <FontAwesomeIcon :class="$style.jobDateIcon" :icon="faCalendarDay" />
                        <span>{{ formattedPostedDate }} </span>
                    </div>
                    <Tag
                        v-if="tagState"
                        :color="tagState.color"
                        :class="$style.tag"
                        :text="tagState.text"
                    />
                    <div v-if="!isJobDescOpen" :class="$style.viewMore">
                        View more
                    </div>
                    <div v-if="isCurrentlyOpenJob && isJobDescOpen" :class="$style.viewingJob">
                        Viewing
                        <FontAwesomeIcon :icon="faChevronRight" :class="$style.viewingChevron" />
                    </div>
                </div>
            </div>
        </div>
    </a>
</template>

<script>
import { Button } from '@applied/marmot'
import Tag from '@/components/Tag/Tag.vue'
import { differenceInDays, formatDistanceToNow, parseISO } from 'date-fns'
import { faMapMarkerAlt, faMoneyBill, faCalendarDay, faChevronRight } from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { currency, job as jobUtils } from 'sharedUtils'
import mapIcons from '@/utils/mapIcons'

export default {
    name: 'JobCard',
    components: {
        FontAwesomeIcon,
        Button,
        Tag,
    },
    props: {
        job: Object,
        isJobDescOpen: {
            type: Boolean,
            default: false,
        },
        currentlyOpenJobId: {
            type: Number,
            default: null,
        }
    },
    data() {
        return {
            showLogo: true,
        }
    },
    computed: {
        ...mapIcons({ faCalendarDay, faMapMarkerAlt, faMoneyBill, faChevronRight }),
        closesSoon() {
            if (!this.job.closes) {
                return false
            }
            return differenceInDays(new Date(this.job.closes), new Date()) <= 5
        },
        formattedPostedDate() {
            return formatDistanceToNow(parseISO(this.job.createdAt), {
                addSuffix: true,
            })
        },
        jobAgeInDays() {
            return Math.abs(differenceInDays(new Date(this.job.createdAt), new Date()))
        },
        newDate() {
            return this.jobAgeInDays === 0 ? 'today' : `${this.jobAgeInDays}d`
        },
        justPosted() {
            return this.jobAgeInDays <= 5
        },
        tagState() {
            if (this.closesSoon) {
                return { color: 'yellow', text: 'Closes soon' }
            }
            if (this.justPosted) {
                return { color: 'green', text: 'Just posted' }
            }
            return null
        },
        locations() {
            return this.job.JobLocations.map(jobLocation => jobLocation.Location)
        },
        jobLocationDescriptor() {
            return jobUtils.getJobLocationsDescriptor({
                locations: this.locations,
                defaultDesc: this.job.location,
            })
        },
        hasJobLocation() {
            return Boolean(this.jobLocationDescriptor.mainLocationDesc)
        },
        jobLocationDescription() {
            const descriptor = this.jobLocationDescriptor
            return `${descriptor.mainLocationDesc} ${descriptor.otherLocationsDesc}`
        },
        jobLocationDetails() {
            const separator = ' · '

            if (this.jobLocationDescriptor.type === 'multiple countries') {
                return jobUtils
                    .getUniqueCountriesList({
                        locations: this.locations,
                    })
                    .join(separator)
            }
            return this.jobLocationDescriptor.formattedSublocationList.join(separator)
        },
        orderedJobSeniorities() {
            return this.job.JobSeniorities.map(js => js.Seniority).sort((a, b) => a.order - b.order)
        },
        seniority() {
            if (!this.orderedJobSeniorities) {
                return null
            }

            return this.orderedJobSeniorities.map(seniority => seniority.name).join(', ')
        },
        hasSalary() {
            return (
                this.job.salaryCurrency && (this.job.salaryLowerBound || this.job.salaryUpperBound)
            )
        },
        hasSalaryDetails() {
            return Boolean(this.job.package)
        },
        salary() {
            if (this.hasSalary) {
                return currency.formatSalaryRange({
                    lowerBound: this.job.salaryLowerBound,
                    upperBound: this.job.salaryUpperBound,
                    currencyCode: this.job.salaryCurrency,
                    locale: 'en-GB',
                })
            }

            if (this.hasSalaryDetails) {
                return 'View for more details'
            }

            return null
        },
        isCurrentlyOpenJob() {
            return this.currentlyOpenJobId === this.job.id
        },
    },
    methods: {
        emitOpenJob(job) {
            this.$emit('openJob', job)
        },
    },
}
</script>

<style module src="./JobCard.css" />
